<template>
	<el-row>
		<el-col :span="24" style="text-align: left;">
			<div class="base-form header">
				<h2>Модели автомобилей</h2>		
				<el-button type="primary" :icon="circleIcon" size="large" @click="onAddVehicleModelClick">
					Новая модель автомобиля
				</el-button>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="4">			
			<div class="base-form filter">
				<el-input
					v-model="tableFilterSearchQuery"	
					:readonly="tableLoading"
					clearable	
					max="250"			
					placeholder="Поиск модели или марки автомобиля по названию">
					<template #append>
						<el-button :icon="searchIcon" />
					</template>
				</el-input>
			</div>
		</el-col>
		<el-col :span="4">
			<div class="base-form filter">
				<catalog-picker-component 
					ref="manufacturer"
					auth
					v-model="tableFilterManufacturer"
					placeholder="Поиск марок автомобилей по названию"
					search-url="/api/vehiclemanufacturers/search"					
					@update:modelValue="fetchVehicleModelsAsync">
					<template #label>
						{{ tableFilterManufacturer?.name }}
					</template>
					<template #default="scope">
						{{ scope.name }}
					</template>
				</catalog-picker-component>
			</div>
		</el-col>
	</el-row>

	<el-row>
		<el-col :span="24">
			<el-table
				row-key="id" 
				:data="tableData"
				v-loading="tableLoading"
				sortable="custom"				
				:default-sort="{ prop: tableSortColumn, order: tableSortOrder }"
				@sort-change="onTableSortingChange"
				@row-dblclick="onTableRowDblClick">
				<template #empty>
					<el-text type="info">Список моделей автомобилей пуст ☹️</el-text>
				</template>
				<el-table-column v-if="isPickerMode" width="100">
					<template #default="scope">
						<el-button
							size="small"
							type="primary"
							@click="(e) => onTablePickVehicleClick(scope.row, e)">
							Выбрать
						</el-button>
					</template>
				</el-table-column>
				<el-table-column label="Модель" prop="name" sortable>
					<template #default="scope">
						<el-link 
							:underline="false" 
							type="primary"
							@click="(e) => onTableVehicleModelCellClick(scope.row, e)">							
							{{ scope.row.name }}
						</el-link>
					</template>
				</el-table-column>
				<el-table-column label="Производитель">
					<template #default="scope">
						<div>
							{{ scope.row.manufacturer.name }}
						</div>
					</template>
				</el-table-column>
				<el-table-column>
					<template #default="scope">
						<div style="text-align: right;">
							<el-button
								:icon="deleteIcon"
								size="small"
								type="danger"
								circle
								@click="(e) => onTableDeleteVehicleModelClick(scope.row, e)">
							</el-button>
						</div>
					</template>
				</el-table-column>
			</el-table>
		</el-col>
	</el-row>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import store from '@/store';
import { CirclePlus, Check, Search, Delete } from '@element-plus/icons-vue';
import { ElButton, ElTable, ElTag, ElLink, ElMessageBox, ElNotification } from 'element-plus';
import EventHelper from '@/helpers/eventsHelper';
import VehicleModel from '@/models/VehicleModel';
import TabsDictionary from '@/variables/tabs';
import PickerConfigurationModel from '@/models/PickerConfigurationModel';
import ApplicationTab from '@/models/ApplicationTab';
import VehicleModelsApiHelper from '@/helpers/api/VehicleModelsApiHelper';
import { watch, WatchStopHandle } from 'vue';
import VehicleManufacturer from '@/models/VehicleManufacturer';
import CatalogPickerComponent from '@/components/CatalogPickerComponent.vue';

@Options({
	components: {
		CirclePlus, ElButton, ElTable, ElTag, ElLink,
		CatalogPickerComponent
	}, 
	props: {
		params: Array
	},
})
export default class VehicleModelsView extends Vue {
	/** constants */		
	circleIcon = CirclePlus;
	deleteIcon = Delete;
	searchIcon = Search;
	checkIcon = Check;
	tableLoadingSvg = `<path class="path" d="M 30 15 L 28 17 M 25.61 25.61 A 15 15, 0, 0, 1, 15 30 A 15 15, 0, 1, 1, 27.99 7.5 L 15 15" style="stroke-width: 4px; fill: rgba(0, 0, 0, 0)"/>`;
	reloadTableEvent = 'vehicleModelsListUpdate';
	vehicleModelsApiHelper = new VehicleModelsApiHelper(store.getters.jwt);

	/** props */	
	params!: Array<any>;

	/** view-models */
	tableLoading = false;	
	tableSortColumn = 'name';
	tableSortOrder = 'ascending';
	tableFilterSearchQuery: string | null = null;
	tableFilterManufacturer: VehicleManufacturer | null = null;

	isPickerMode = false;
	onPickedCallback: Function | null = null;
	tableData: Array<VehicleModel> = [];

	tableFilterSearchQueryWatcher?: WatchStopHandle;

	mounted() {
		this.tableFilterSearchQueryWatcher = watch(
			() => this.tableFilterSearchQuery,
			() => this.fetchVehicleModelsAsync()
		);

		const pickerModeArgument = this.params?.length ? this.params[0] as PickerConfigurationModel : null;
		
		/** check if picker mode activated */
		if (pickerModeArgument && 
			pickerModeArgument.isPickerModeEnabled === true &&
			pickerModeArgument.onItemPickedCallback) {
			this.isPickerMode = true;
			this.onPickedCallback = pickerModeArgument.onItemPickedCallback;
		}

		EventHelper.setEventListener(this.reloadTableEvent, this.fetchVehicleModelsAsync);
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	unmounted() {
		if (this.tableFilterSearchQueryWatcher) {
			this.tableFilterSearchQueryWatcher();
		}
		EventHelper.removeEventListener(this.reloadTableEvent, this.fetchVehicleModelsAsync);
	}

	onTableSortingChange(eventObject: any) {
		this.tableSortColumn = eventObject.prop;
		this.tableSortOrder = eventObject.order;
		EventHelper.dispatchEvent(this.reloadTableEvent);
	}
	
	onTableRowDblClick(vehicleModel: VehicleModel) {
		const orderViewTab = TabsDictionary.vehicleModelView;
		orderViewTab.params = [ vehicleModel, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableVehicleModelCellClick(vehicleModel: VehicleModel, e: Event) {
		e.stopPropagation();

		const orderViewTab = TabsDictionary.vehicleModelView;
		orderViewTab.params = [ vehicleModel, true ];
		orderViewTab.allowMultiple = true;
		store.commit('addTab', orderViewTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onTableDeleteVehicleModelClick(vehicle: VehicleModel, e: Event) {
		e.stopPropagation();
			
		this.vehicleModelsApiHelper
			.isDeletableAsync(vehicle.id)
			.then(deletable => {
				if (!deletable) {
					ElMessageBox.confirm(
						'Удаление невозможно, модель используется в автомобилях контрагентов', {
						confirmButtonText: 'OK',
						type: 'error',
					});
				} else {
					ElMessageBox.confirm(
						`Модель ${vehicle.name} (${vehicle.manufacturer?.name}) будет удалена. Продолжить ?`, {
						confirmButtonText: 'OK',
						cancelButtonText: 'Отмена',
						type: 'warning',
					})
					.then(() => this.deleteVehicleModelsAsync(vehicle.id));
				}
		});
	}

	async fetchVehicleModelsAsync() {
		try {
			this.tableLoading = true;
			const vehicleModels = await this.vehicleModelsApiHelper.getModelsAsync(
				this.tableSortColumn,
				this.tableSortOrder,
				this.tableFilterSearchQuery,
				this.tableFilterManufacturer ? [this.tableFilterManufacturer.id] : null
			);
			if (!vehicleModels) {
				ElNotification({
					message: 'Ошибка загрузки списка моделей автомобиля',
					type: 'error',
				});				
			} else {
				this.tableData = vehicleModels!;
			}
		} finally {
			this.tableLoading = false;
		}
	}

	async deleteVehicleModelsAsync(vehicleModelId: number) {		
		this.tableLoading = true;
		const isDeleted = await this.vehicleModelsApiHelper.deleteAsync(vehicleModelId);
		if (!isDeleted) {
			ElNotification({
				message: 'Ошибка удаление списка автомобилей',
				type: 'error',
			});
			return;
		}

		EventHelper.dispatchEvent(this.reloadTableEvent);
	}

	onAddVehicleModelClick() {
		const appTab = TabsDictionary.vehicleModelView;
		appTab.params = null;
		store.commit('addTab', appTab);
		const newTab = store.getters.newlyAddedTab as ApplicationTab;
		store.commit('changeTab', newTab.id);
	}

	onEditVehicleModelClick(vehicleModel: VehicleModel) {
		const appTab = TabsDictionary.vehicleModelView;
		appTab.params = [ vehicleModel ];
		store.commit('addTab', appTab);
		store.commit('changeTab', appTab.view);
	}

	/** handles callback and passes selected vehicle model to a parent component (if picker-mode is enabled) */	
	onTablePickVehicleClick(vehicleModel: VehicleModel, e: Event) {
		e.stopPropagation();
		if (this.onPickedCallback) {
			this.onPickedCallback(vehicleModel);
		}
	}
}
</script>